import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Save, User, Phone, Users, Settings, MapPin, Shield, FileText, ChevronDown, GripVertical, Building, Award } from 'lucide-react';
import { SearchBar } from '../shared/SearchBar';
import { SortableTableHeader } from '../shared/SortableTableHeader';
import { Pagination } from '../shared/Pagination';
import { ConfirmModal } from '../../../framework/modals/ConfirmModal';
import { PhoneInput } from '../shared/PhoneInput';
import { Checkbox } from '../../../components/ui/Checkbox';
import { CustomerDropdown } from '../shared/CustomerDropdown';
import { AgeInput } from '../shared/AgeInput';
import { FormInput } from '../shared/FormInput';
import { TiptapEditor } from '../../../components/ui/TiptapEditor';
import { CollapsibleRichText } from '../../../components/ui/CollapsibleRichText';
import { hasRichTextContent, normalizeRichTextHtml } from '../../../utils/richText';
import { useProviderManagement } from '../hooks/useProviderManagement';
import { useModeratorData } from '../hooks/useModeratorData';
import { useTableSort } from '../hooks/useTableSort';
import { useModeratorPermissions } from '../hooks/useModeratorPermissions';
import type { Customer } from '../types/moderator.types';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Note Item Component
interface SortableNoteItemProps {
  id: string;
  note: string;
  index: number;
  onDelete: (index: number) => void;
}

const SortableNoteItem: React.FC<SortableNoteItemProps> = ({ id, note, index, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      className="rounded-lg border-l-4 p-4 shadow-sm transition-all duration-200 hover:shadow-md group"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderLeftColor: 'var(--color-accent)',
        borderColor: 'var(--color-border)',
        ...style
      }}
    >
      <div className="flex items-start justify-between space-x-4 pt-1 pb-0">
        <div className="flex items-start space-x-3 flex-1">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-opacity-20 hover:bg-gray-400 transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <GripVertical className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <CollapsibleRichText
              content={note}
              style={{ color: 'var(--color-text-primary)' }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => onDelete(index)}
          className="opacity-0 group-hover:opacity-100 p-2 rounded-lg transition-all duration-200 hover:bg-red-100 dark:hover:bg-red-900/20"
          style={{ color: 'var(--color-error)' }}
          title="Delete note"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const ProvidersSection: React.FC = () => {
  const { providers, customers, locations, conditions, insurances, languages, refetchModeratorData } = useModeratorData();
  const provider = useProviderManagement(refetchModeratorData);
  const { canAdd, canChange, canDelete } = useModeratorPermissions();

  // Customer filter state (separate from form's customerId)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const canCreateProvider = selectedCustomer
    ? canAdd(selectedCustomer.id)
    : customers.some((customer) => canAdd(customer.id));

  // Local state for form dropdowns and search
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showInsuranceDropdown, setShowInsuranceDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [insuranceSearchTerm, setInsuranceSearchTerm] = useState('');
  const [languageSearchTerm, setLanguageSearchTerm] = useState('');
  const [specialtySearchTerm, setSpecialtySearchTerm] = useState('');
  const [appSearchTerm, setAppSearchTerm] = useState('');
  const [showAppDropdown, setShowAppDropdown] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteEditorKey, setNoteEditorKey] = useState(0);
  const [showAgeDisclaimer, setShowAgeDisclaimer] = useState(false);

  // Sync showAgeDisclaimer when editing a provider
  useEffect(() => {
    if (provider.editing) {
      setShowAgeDisclaimer(!!provider.editing.ageRestrictionNotes && provider.editing.ageRestrictionNotes.length > 0);
    } else {
      setShowAgeDisclaimer(false);
    }
  }, [provider.editing]);

  useEffect(() => {
    if (provider.showForm) {
      setNewNote('');
      setNoteEditorKey((key) => key + 1);
    }
  }, [provider.showForm, provider.editing?.id]);

  // Keep editing provider in sync with refetched data
  useEffect(() => {
    if (provider.editing?.id && providers.length > 0) {
      const updated = providers.find(p => p.id === provider.editing!.id);
      if (updated) provider.setEditing(updated);
    }
  }, [providers]);

  // Drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.location-dropdown') && !target.closest('.insurance-dropdown') && 
          !target.closest('.specialty-dropdown') && !target.closest('.customer-dropdown') &&
          !target.closest('.app-dropdown')) {
        setShowLocationDropdown(false);
        setShowInsuranceDropdown(false);
        setShowSpecialtyDropdown(false);
        setShowCustomerDropdown(false);
        setShowAppDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredProviders = providers.filter(prov => {
    const matchesSearch = `${prov.firstName} ${prov.lastName}`.toLowerCase().includes(provider.searchTerm.toLowerCase());
    // If no customer selected, show all providers. Otherwise, filter by customer
    const matchesCustomer = !selectedCustomer || prov.customerId === selectedCustomer.id;
    return matchesSearch && matchesCustomer;
  });

  const {
    paginatedData,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    itemsPerPage,
    handleSort,
    setCurrentPage,
    setItemsPerPage
  } = useTableSort(filteredProviders, 'customer.name', 10);

  // Filtered arrays for dropdown search
  // Filter locations by selected customer first, then by search term
  const customerLocations = provider.form.customerId
    ? locations.filter(loc => loc.customerId === provider.form.customerId)
    : [];

  const filteredLocations = locationSearchTerm.trim() === ''
    ? customerLocations
    : customerLocations.filter((location) =>
        location.name.toLowerCase().includes(locationSearchTerm.toLowerCase()) ||
        (location.state &&
         (location.state.name.toLowerCase().includes(locationSearchTerm.toLowerCase()) ||
          location.state.code.toLowerCase().includes(locationSearchTerm.toLowerCase())))
      );

  // Filter insurances to only those with the same customer as the provider (same as locations)
  const customerInsurances = provider.form.customerId
    ? insurances.filter(ins => ins.customerId === provider.form.customerId)
    : [];

  const filteredInsurances = insuranceSearchTerm.trim() === ''
    ? customerInsurances
    : customerInsurances.filter((insurance) =>
        insurance.name.toLowerCase().includes(insuranceSearchTerm.toLowerCase())
      );

  // For dropdown: only show insurances not already selected (so user can add more)
  const availableInsurancesForDropdown = filteredInsurances.filter(
    (insurance) => !provider.form.insuranceIds.includes(insurance.id)
  );

  const filteredLanguages = languageSearchTerm.trim() === ''
    ? languages
    : languages.filter((language) => 
        language.name.toLowerCase().includes(languageSearchTerm.toLowerCase())
      );

  // Filter providers for APP selection - by customer and search term
  const customerProviders = provider.form.customerId
    ? providers.filter(p => p.customerId === provider.form.customerId && p.isActive)
    : [];
  
  const filteredApps = appSearchTerm.trim() === ''
    ? customerProviders.filter(p => 
        p.id !== provider.editing?.id && 
        !provider.form.appIds.includes(p.id)
      )
    : customerProviders.filter(p => {
        const matchesSearch = `${p.firstName} ${p.lastName} ${p.title || ''}`.toLowerCase().includes(appSearchTerm.toLowerCase());
        return matchesSearch && 
               p.id !== provider.editing?.id && 
               !provider.form.appIds.includes(p.id);
      });

  const filteredConditions = specialtySearchTerm.trim() === ''
    ? conditions
    : conditions.filter((condition) => 
        condition.name.toLowerCase().includes(specialtySearchTerm.toLowerCase())
      );

  // Group filtered conditions by category
  const groupedConditions = filteredConditions.reduce((acc, condition) => {
    const categoryName = condition.category?.name || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(condition);
    return acc;
  }, {} as Record<string, typeof filteredConditions>);

  // Sort categories: Uncategorized last, others alphabetically
  const sortedCategoryNames = Object.keys(groupedConditions).sort((a, b) => {
    if (a === 'Uncategorized') return 1;
    if (b === 'Uncategorized') return -1;
    return a.localeCompare(b);
  });

  // Toggle functions
  const toggleLocation = (locationId: string) => {
    provider.setForm(prev => ({
      ...prev,
      locationIds: prev.locationIds.includes(locationId)
        ? prev.locationIds.filter(id => id !== locationId)
        : [...prev.locationIds, locationId]
    }));
  };

  const toggleInsurance = (insuranceId: string) => {
    provider.setForm(prev => ({
      ...prev,
      insuranceIds: prev.insuranceIds.includes(insuranceId)
        ? prev.insuranceIds.filter(id => id !== insuranceId)
        : [...prev.insuranceIds, insuranceId]
    }));
  };

  const toggleLanguage = (languageId: string) => {
    provider.setForm(prev => ({
      ...prev,
      languageIds: prev.languageIds.includes(languageId)
        ? prev.languageIds.filter(id => id !== languageId)
        : [...prev.languageIds, languageId]
    }));
  };

  const toggleSpecialty = (specialtyId: string) => {
    provider.setForm(prev => {
      const newSpecialtyIds = prev.specialtyIds.includes(specialtyId)
        ? prev.specialtyIds.filter(id => id !== specialtyId)
        : [...prev.specialtyIds, specialtyId];
      
      return {
        ...prev,
        specialtyIds: newSpecialtyIds
      };
    });
    // Clear validation error when specialties are selected
    if (provider.validationError && provider.validationError.includes('specialty')) {
      // Note: We can't directly set validationError here, but it will be cleared on next submit attempt
    }
  };

  // Handle drag end for note reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && over) {
      const oldIndex = provider.form.notes.findIndex((_, index) => `note-${index}` === active.id);
      const newIndex = provider.form.notes.findIndex((_, index) => `note-${index}` === over.id);

      const reorderedNotes = arrayMove(provider.form.notes, oldIndex, newIndex);
      provider.setForm(prev => ({ ...prev, notes: reorderedNotes }));
    }
  };

  const addProviderNote = () => {
    const content = normalizeRichTextHtml(newNote);
    if (!hasRichTextContent(content)) {
      return;
    }
    provider.setForm(prev => ({
      ...prev,
      notes: [...prev.notes, content],
    }));
    setNewNote('');
    setNoteEditorKey((key) => key + 1);
  };

  const noteHasContent = hasRichTextContent(newNote);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Provider Management
        </h2>
        {canCreateProvider && (
        <button
          onClick={() => {
            provider.handleCreate();
            setShowAgeDisclaimer(false);
          }}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'white'
          }}
        >
          <Plus className="w-4 h-4" />
          <span>Add Provider</span>
        </button>
        )}
      </div>

      {/* Customer Selection */}
      <CustomerDropdown
        customers={customers}
        selectedCustomer={selectedCustomer}
        onSelectCustomer={(customer) => {
          setSelectedCustomer(customer);
          provider.setSearchTerm('');
        }}
        helperText={selectedCustomer 
          ? `Showing providers for ${selectedCustomer.name}` 
          : 'Showing all providers'}
      />

      <SearchBar
        value={provider.searchTerm}
        onChange={provider.setSearchTerm}
        placeholder="Search providers..."
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        totalItems={filteredProviders.length}
      />

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
              <SortableTableHeader
                field="customer.name"
                label="Customer"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableTableHeader
                field="firstName"
                label="Name"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableTableHeader
                field="isActive"
                label="Status"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th className="text-right p-3" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((prov) => (
              <tr key={prov.id} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                  {prov.customer?.name || '-'}
                </td>
                <td className="p-3" style={{ color: 'var(--color-text-primary)' }}>
                  {prov.firstName} {prov.lastName}{prov.title ? `, ${prov.title}` : ''}
                </td>
                <td className="p-3">
                  <span
                    className="px-2 py-1 rounded text-xs"
                    style={{
                      backgroundColor: prov.isActive ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
                      color: prov.isActive ? 'var(--color-success)' : 'var(--color-error)'
                    }}
                  >
                    {prov.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end space-x-2">
                    {canChange(prov.customerId) && (
                    <button
                      onClick={() => provider.handleEdit(prov)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-accent)' }}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    )}
                    {canDelete(prov.customerId) && (
                    <button
                      onClick={() => provider.handleDelete(prov.id)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-error)' }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={provider.showDeleteModal}
        onClose={provider.cancelDelete}
        onConfirm={provider.confirmDelete}
        title="Delete Provider"
        message="Are you sure you want to delete this provider? This action cannot be undone."
        type="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Provider Form Modal - Full Version */}
      {provider.showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div 
            className="rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto custom-scrollbar border relative"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              boxShadow: 'var(--shadow-xl)'
            }}
          >
            {/* Modal Header */}
            <div 
              className="sticky top-0 rounded-t-2xl p-6 pb-4 z-[70]"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderBottom: '1px solid var(--color-border)'
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div 
                    className="p-2 rounded-lg"
                    style={{
                      backgroundColor: 'var(--color-bg-tertiary)'
                    }}
                  >
                    <Users className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      {provider.editing ? 'Edit Provider' : 'Add New Provider'}
                    </h3>
                    <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                      {provider.editing ? 'Update provider information and settings' : 'Create a new provider profile'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    provider.handleCancel();
                    setLocationSearchTerm('');
                    setInsuranceSearchTerm('');
                    setLanguageSearchTerm('');
                    setSpecialtySearchTerm('');
                    setShowCustomerDropdown(false);
                  }}
                  className="p-2 rounded-lg transition-colors duration-200"
                  style={{
                    color: 'var(--color-text-muted)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                  }}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              {provider.validationError && (
                <div 
                  className="mb-6 p-4 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--color-error-bg)',
                    borderColor: 'var(--color-error)',
                    color: 'var(--color-error)'
                  }}
                >
                  <p className="text-sm font-medium">{provider.validationError}</p>
                </div>
              )}
              <form
                onSubmit={(e) => provider.handleSubmit(e, conditions)}
                onKeyDown={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('.tiptap') || target.closest('.ProseMirror')) {
                    e.stopPropagation();
                  }
                }}
                className="space-y-8"
              >
                {/* Basic Information */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <User className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Basic Information</h4>
                  </div>

                  {/* Customer Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Customer *</label>
                    <div className="relative customer-dropdown overflow-visible">
                      <div 
                        className="min-h-[40px] border rounded-lg p-2 cursor-pointer transition-colors"
                        style={{
                          borderColor: 'var(--color-border)',
                          backgroundColor: 'var(--color-bg-primary)'
                        }}
                        onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-accent)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-border)';
                        }}
                      >
                        {!provider.form.customerId ? (
                          <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Select a customer...</div>
                        ) : (
                          <div className="flex items-center">
                            <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                              {customers.find(c => c.id === provider.form.customerId)?.name}
                            </span>
                          </div>
                        )}
                        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
                      </div>
                      
                      {showCustomerDropdown && (
                        <div 
                          className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)'
                          }}
                        >
                          {customers.filter(customer => customer.isActive).map((customer) => (
                            <div
                              key={customer.id}
                              className="px-3 py-2 cursor-pointer hover:bg-opacity-10 transition-colors"
                              style={{
                                color: 'var(--color-text-primary)',
                                backgroundColor: provider.form.customerId === customer.id ? 'var(--color-accent)' : 'transparent'
                              }}
                              onClick={() => {
                                provider.handleCustomerChange(customer.id);
                                setShowCustomerDropdown(false);
                              }}
                            >
                              <div className="text-sm">{customer.name}</div>
                              {customer.description && (
                                <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                                  {customer.description}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      Select the customer organization this provider belongs to
                    </p>
                  </div>

                  {/* Only show form fields after customer is selected */}
                  {provider.form.customerId ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormInput
                      label="First Name"
                      required
                      type="text"
                      value={provider.form.firstName}
                      onChange={(e) => provider.setForm(prev => ({ ...prev, firstName: e.target.value }))}
                      backgroundColor="primary"
                    />
                    
                    <FormInput
                      label="Last Name"
                      required
                      type="text"
                      value={provider.form.lastName}
                      onChange={(e) => provider.setForm(prev => ({ ...prev, lastName: e.target.value }))}
                      backgroundColor="primary"
                    />
                    
                    <FormInput
                      label="Title"
                      type="text"
                      value={provider.form.title}
                      onChange={(e) => provider.setForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Dr., NP, PA-C, etc."
                      backgroundColor="primary"
                    />
                  </div>
                  
                  {/* Specialties */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Specialties *</label>
                    <div className="relative specialty-dropdown overflow-visible">
                      <div 
                        className="min-h-[40px] border rounded-lg p-2 cursor-pointer transition-colors"
                        style={{
                          borderColor: 'var(--color-border)',
                          backgroundColor: 'var(--color-bg-primary)'
                        }}
                        onClick={() => setShowSpecialtyDropdown(!showSpecialtyDropdown)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-accent)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-border)';
                        }}
                      >
                        {provider.form.specialtyIds.length === 0 ? (
                          <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Select specialties...</div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {provider.form.specialtyIds.map((specialtyId: string) => {
                              const condition = conditions.find(c => c.id === specialtyId);
                              return condition ? (
                                <span 
                                  key={specialtyId} 
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: 'var(--color-bg-tertiary)',
                                    color: 'var(--color-text-primary)'
                                  }}
                                >
                                  {condition.name}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleSpecialty(specialtyId);
                                    }}
                                    className="ml-1 hover:opacity-70 transition-opacity"
                                    style={{ color: 'var(--color-accent)' }}
                                  >
                                    ×
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
                      </div>
                      {showSpecialtyDropdown && (
                        <div 
                          className="absolute z-[75] w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-y-auto custom-scrollbar"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            boxShadow: 'var(--shadow-lg)'
                          }}
                        >
                          <div 
                            className="sticky top-0 border-b p-2"
                            style={{
                              backgroundColor: 'var(--color-bg-primary)',
                              borderColor: 'var(--color-border)'
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Search specialties..."
                              value={specialtySearchTerm}
                              onChange={(e) => setSpecialtySearchTerm(e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded focus:ring-1 transition-colors"
                              style={{
                                backgroundColor: 'var(--color-bg-primary)',
                                borderColor: 'var(--color-border)',
                                color: 'var(--color-text-primary)',
                                '--tw-ring-color': 'var(--color-accent)'
                              } as React.CSSProperties}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          {filteredConditions.length === 0 ? (
                            <div className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>No specialties found</div>
                          ) : (
                            sortedCategoryNames.map((categoryName) => (
                              <div key={categoryName}>
                                {/* Category Header */}
                                <div 
                                  className="px-3 py-2 text-xs font-semibold sticky top-0"
                                  style={{
                                    backgroundColor: 'var(--color-bg-secondary)',
                                    color: 'var(--color-text-secondary)',
                                    borderBottom: '1px solid var(--color-border)',
                                    borderTop: categoryName !== sortedCategoryNames[0] ? '1px solid var(--color-border)' : 'none'
                                  }}
                                >
                                  {categoryName}
                                </div>
                                {/* Conditions in this category */}
                                {groupedConditions[categoryName]?.map((condition) => (
                                  <div
                                    key={condition.id}
                                    onClick={() => toggleSpecialty(condition.id)}
                                    className="px-3 py-2 cursor-pointer text-sm transition-colors pl-6"
                                    style={{
                                      backgroundColor: provider.form.specialtyIds.includes(condition.id) ? 'var(--color-bg-secondary)' : 'transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!provider.form.specialtyIds.includes(condition.id)) {
                                        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!provider.form.specialtyIds.includes(condition.id)) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }
                                    }}
                                  >
                                    <span style={{ color: 'var(--color-text-primary)' }}>{condition.name}</span>
                                    {provider.form.specialtyIds.includes(condition.id) && (
                                      <span className="ml-2" style={{ color: 'var(--color-accent)' }}>✓</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {provider.form.specialtyIds.length === 0 && (
                      <p className="text-xs mt-1" style={{ color: 'var(--color-error)' }}>Please select at least one specialty</p>
                    )}
                  </div>
                    </>
                  ) : null}
                </div>

                {/* Credentials - Only show when customer is selected */}
                {provider.form.customerId ? (
                <>
                {/* Credentials */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <Award className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Credentials</h4>
                  </div>

                  <FormInput
                    label="NPI"
                    type="text"
                    value={provider.form.npiNumber}
                    onChange={(e) => provider.setForm(prev => ({ ...prev, npiNumber: e.target.value }))}
                    placeholder="Enter NPI number"
                    backgroundColor="primary"
                  />
                </div>

                {/* APP Management Section */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <Users className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>APP Management</h4>
                  </div>

                  <div className="space-y-6">
                    {/* Section 1: Supervising Doctor(s) as pills with remove */}
                    {(() => {
                      const list = provider.editing?.supervisingProviders?.length
                        ? provider.editing.supervisingProviders
                        : provider.editing?.supervisingProvider
                          ? [provider.editing.supervisingProvider]
                          : (provider.form.supervisingProviderId && providers.find(p => p.id === provider.form.supervisingProviderId))
                            ? [providers.find(p => p.id === provider.form.supervisingProviderId)!]
                            : [];
                      if (list.length === 0) return null;
                      const appId = provider.editing?.id;
                      return (
                        <div>
                          <label
                            className="block text-sm font-medium mb-2"
                            style={{ color: 'var(--color-text-secondary)' }}
                          >
                            {list.length === 1 ? 'Doctor' : 'Doctors'}
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {list.map((d) => (
                              <span
                                key={d.id}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                                style={{
                                  backgroundColor: 'var(--color-bg-tertiary)',
                                  color: 'var(--color-text-primary)',
                                  borderWidth: 1,
                                  borderStyle: 'solid',
                                  borderColor: 'var(--color-border)'
                                }}
                              >
                                {d.firstName} {d.lastName}{d.title ? `, ${d.title}` : ''}
                                {appId && (
                                  <button
                                    type="button"
                                    onClick={() => provider.removeSupervisingDoctor(appId, d.id)}
                                    className="rounded-full p-0.5 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1"
                                    style={{
                                      color: 'var(--color-text-muted)',
                                      opacity: 0.8
                                    }}
                                    title="Remove doctor"
                                    aria-label={`Remove ${d.firstName} ${d.lastName}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Section 2: Manage APPs (for supervising providers) */}
                    <div>
                      <label 
                        className="block text-sm font-medium mb-2"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        APPs under this provider
                      </label>
                      <div className="space-y-2">
                        {!provider.form.customerId ? (
                          <p className="text-sm p-3 rounded-lg border" style={{ 
                            color: 'var(--color-text-secondary)',
                            borderColor: 'var(--color-border)',
                            backgroundColor: 'var(--color-bg-primary)'
                          }}>
                            Please select a customer first to add APPs
                          </p>
                        ) : (
                          <div className="relative app-dropdown overflow-visible">
                            <div 
                              className="w-full px-4 py-3 border rounded-lg text-sm transition-colors cursor-pointer"
                              style={{
                                backgroundColor: 'var(--color-bg-primary)',
                                borderColor: showAppDropdown ? 'var(--color-accent)' : 'var(--color-border)',
                                color: 'var(--color-text-primary)'
                              }}
                              onClick={() => {
                                setShowAppDropdown(!showAppDropdown);
                                if (!showAppDropdown) {
                                  setAppSearchTerm('');
                                }
                              }}
                              onMouseEnter={(e) => {
                                if (!showAppDropdown) {
                                  e.currentTarget.style.borderColor = 'var(--color-accent)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!showAppDropdown) {
                                  e.currentTarget.style.borderColor = 'var(--color-border)';
                                }
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <span style={{ color: appSearchTerm ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
                                  {appSearchTerm || 'Add an APP...'}
                                </span>
                                <ChevronDown className="w-4 h-4 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
                              </div>
                            </div>
                            {showAppDropdown && (
                              <div 
                                className="absolute z-[75] w-full mt-1 border rounded-lg shadow-lg max-h-96 overflow-y-auto custom-scrollbar"
                                style={{
                                  backgroundColor: 'var(--color-bg-primary)',
                                  borderColor: 'var(--color-border)',
                                  boxShadow: 'var(--shadow-lg)'
                                }}
                              >
                                <div 
                                  className="sticky top-0 border-b p-2"
                                  style={{
                                    backgroundColor: 'var(--color-bg-primary)',
                                    borderColor: 'var(--color-border)'
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="Search providers..."
                                    value={appSearchTerm}
                                    onChange={(e) => setAppSearchTerm(e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded focus:ring-1 transition-colors"
                                    style={{
                                      backgroundColor: 'var(--color-bg-primary)',
                                      borderColor: 'var(--color-border)',
                                      color: 'var(--color-text-primary)'
                                    }}
                                    onFocus={(e) => {
                                      e.currentTarget.style.borderColor = 'var(--color-accent)';
                                    }}
                                    onBlur={(e) => {
                                      e.currentTarget.style.borderColor = 'var(--color-border)';
                                    }}
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                <div className="p-1">
                                  {filteredApps.length === 0 ? (
                                    <div className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                      {appSearchTerm ? 'No providers found' : 'No providers available for this customer'}
                                    </div>
                                  ) : (
                                    filteredApps.map((app) => (
                                      <div
                                        key={app.id}
                                        className="px-3 py-2 text-sm cursor-pointer rounded transition-colors"
                                        style={{
                                          color: 'var(--color-text-primary)'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!provider.form.appIds.includes(app.id)) {
                                            provider.setForm(prev => ({
                                              ...prev,
                                              appIds: [...prev.appIds, app.id]
                                            }));
                                          }
                                          setShowAppDropdown(false);
                                          setAppSearchTerm('');
                                        }}
                                      >
                                        {app.firstName} {app.lastName}{app.title ? ` - ${app.title}` : ''}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {provider.form.appIds.length > 0 ? (
                          [...provider.form.appIds]
                            .map((appId) => {
                              const app = providers.find(p => p.id === appId);
                              return app ? { appId, app } : null;
                            })
                            .filter((item): item is { appId: string; app: typeof providers[0] } => item !== null)
                            .sort((a, b) => {
                              const nameA = `${a.app.firstName} ${a.app.lastName}`.toLowerCase();
                              const nameB = `${b.app.firstName} ${b.app.lastName}`.toLowerCase();
                              return nameA.localeCompare(nameB);
                            })
                            .map(({ appId, app }) => (
                              <div
                                key={appId}
                                className="flex items-center justify-between px-2.5 py-1.5 rounded border"
                                style={{
                                  backgroundColor: 'var(--color-bg-primary)',
                                  borderColor: 'var(--color-border)'
                                }}
                              >
                                <span className="text-xs" style={{ color: 'var(--color-text-primary)' }}>
                                  {app.firstName} {app.lastName}{app.title ? ` - ${app.title}` : ''}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    provider.setForm(prev => ({
                                      ...prev,
                                      appIds: prev.appIds.filter(id => id !== appId)
                                    }));
                                  }}
                                  className="p-0.5 rounded transition-colors hover:opacity-80"
                                  style={{
                                    color: 'var(--color-error)'
                                  }}
                                  title="Remove APP"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))
                        ) : (
                          provider.form.customerId && (
                            <p className="text-xs text-center" style={{ color: 'var(--color-text-secondary)' }}>
                              No APPs assigned
                            </p>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </>
                ) : null}

                {/* Phone Numbers - Only show when customer is selected */}
                {provider.form.customerId ? (
                <>
                {/* Phone Numbers */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{
                          backgroundColor: 'var(--color-bg-tertiary)'
                        }}
                      >
                        <Phone className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                      </div>
                      <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Phone Numbers</h4>
                    </div>
                    <button
                      type="button"
                      onClick={() => provider.setForm(prev => ({
                        ...prev,
                        phoneNumbers: [...prev.phoneNumbers, {
                          id: `phone-${Date.now()}`,
                          name: '',
                          phone: '',
                          locationId: ''
                        }]
                      }))}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={{
                        backgroundColor: 'var(--color-accent)',
                        color: 'var(--color-text-on-accent)',
                        '--tw-ring-color': 'var(--color-accent)',
                        '--tw-ring-offset-color': 'var(--color-bg-primary)'
                      } as React.CSSProperties}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Phone Number
                    </button>
                  </div>
                  
                  {provider.form.phoneNumbers.length === 0 ? (
                    <div className="text-center py-6" style={{ color: 'var(--color-text-muted)' }}>
                      No phone numbers added yet. Click "Add Phone Number" to get started.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {provider.form.phoneNumbers.map((phoneNumber, index) => (
                        <div 
                          key={phoneNumber.id} 
                          className="rounded-lg p-4 border"
                          style={{
                            backgroundColor: 'var(--color-bg-tertiary)',
                            borderColor: 'var(--color-border)'
                          }}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Phone Number #{index + 1}</h5>
                            <button
                              type="button"
                              onClick={() => provider.setForm(prev => ({
                                ...prev,
                                phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== index)
                              }))}
                              className="text-sm font-medium transition-colors"
                              style={{ color: 'var(--color-error)' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--color-error-hover)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--color-error)';
                              }}
                            >
                              Remove
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Name/Title</label>
                              <input
                                type="text"
                                placeholder="e.g., Front Desk, Triage Nurse, MA/LVN"
                                value={phoneNumber.name}
                                onChange={(e) => provider.setForm(prev => ({
                                  ...prev,
                                  phoneNumbers: prev.phoneNumbers.map((pn, i) => 
                                    i === index ? { ...pn, name: e.target.value } : pn
                                  )
                                }))}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                                style={{
                                  backgroundColor: 'var(--color-bg-primary)',
                                  borderColor: 'var(--color-border)',
                                  color: 'var(--color-text-primary)',
                                  '--tw-ring-color': 'var(--color-accent)'
                                } as React.CSSProperties}
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Phone Number</label>
                              <PhoneInput
                                placeholder="(555) 123-4567"
                                value={phoneNumber.phone}
                                onChange={(value) => provider.setForm(prev => ({
                                  ...prev,
                                  phoneNumbers: prev.phoneNumbers.map((pn, i) => 
                                    i === index ? { ...pn, phone: value } : pn
                                  )
                                }))}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                                style={{
                                  backgroundColor: 'var(--color-bg-primary)',
                                  borderColor: 'var(--color-border)',
                                  color: 'var(--color-text-primary)',
                                  '--tw-ring-color': 'var(--color-accent)'
                                } as React.CSSProperties}
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Location</label>
                              <select
                                value={phoneNumber.locationId}
                                onChange={(e) => provider.setForm(prev => ({
                                  ...prev,
                                  phoneNumbers: prev.phoneNumbers.map((pn, i) => 
                                    i === index ? { ...pn, locationId: e.target.value } : pn
                                  )
                                }))}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                                style={{
                                  backgroundColor: 'var(--color-bg-primary)',
                                  borderColor: 'var(--color-border)',
                                  color: 'var(--color-text-primary)',
                                  '--tw-ring-color': 'var(--color-accent)'
                                } as React.CSSProperties}
                              >
                                <option value="">Select Location</option>
                                {customerLocations.map((location) => (
                                  <option key={location.id} value={location.id}>
                                    {location.name}{location.state ? `, ${location.state.code}` : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Staff Contacts */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <Users className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Staff Contacts</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>MA/LVN Name</label>
                        <input
                          type="text"
                          value={provider.form.maLvnName}
                          onChange={(e) => provider.setForm(prev => ({ ...prev, maLvnName: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)',
                            '--tw-ring-color': 'var(--color-accent)'
                          } as React.CSSProperties}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>MA/LVN Phone</label>
                        <PhoneInput
                          value={provider.form.maLvnPhone}
                          onChange={(value) => provider.setForm(prev => ({ ...prev, maLvnPhone: value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)',
                            '--tw-ring-color': 'var(--color-accent)'
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Triage Nurse Name</label>
                        <input
                          type="text"
                          value={provider.form.triageNurseName}
                          onChange={(e) => provider.setForm(prev => ({ ...prev, triageNurseName: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)',
                            '--tw-ring-color': 'var(--color-accent)'
                          } as React.CSSProperties}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Triage Nurse Phone</label>
                        <PhoneInput
                          value={provider.form.triageNursePhone}
                          onChange={(value) => provider.setForm(prev => ({ ...prev, triageNursePhone: value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)',
                            '--tw-ring-color': 'var(--color-accent)'
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Surgery Scheduler Name</label>
                        <input
                          type="text"
                          value={provider.form.surgerySchedulerName}
                          onChange={(e) => provider.setForm(prev => ({ ...prev, surgerySchedulerName: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)',
                            '--tw-ring-color': 'var(--color-accent)'
                          } as React.CSSProperties}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Surgery Scheduler Phone</label>
                        <PhoneInput
                          value={provider.form.surgerySchedulerPhone}
                          onChange={(value) => provider.setForm(prev => ({ ...prev, surgerySchedulerPhone: value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)',
                            '--tw-ring-color': 'var(--color-accent)'
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Practice Admin Name</label>
                        <input
                          type="text"
                          value={provider.form.practiceAdminName}
                          onChange={(e) => provider.setForm(prev => ({ ...prev, practiceAdminName: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)',
                            '--tw-ring-color': 'var(--color-accent)'
                          } as React.CSSProperties}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Practice Admin Phone</label>
                        <PhoneInput
                          value={provider.form.practiceAdminPhone}
                          onChange={(value) => provider.setForm(prev => ({ ...prev, practiceAdminPhone: value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)',
                            '--tw-ring-color': 'var(--color-accent)'
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Provider Settings */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <Settings className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Provider Settings</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <Checkbox
                        id="acceptsNewPatients"
                        label="Accepts New Patients"
                        checked={provider.form.acceptsNewPatients}
                        onChange={(e) => provider.setForm(prev => ({ ...prev, acceptsNewPatients: e.target.checked }))}
                      />
                      <Checkbox
                        id="requiresReferral"
                        label="Requires Referral"
                        checked={provider.form.requiresReferral}
                        onChange={(e) => provider.setForm(prev => ({ ...prev, requiresReferral: e.target.checked }))}
                      />
                      <Checkbox
                        id="isActive"
                        label="Active"
                        checked={provider.form.isActive}
                        onChange={(e) => provider.setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      />
                    </div>
                    <div className="space-y-4">
                      <AgeInput
                        label="Minimum Age"
                        value={provider.form.minimumAge}
                        onChange={(value) => provider.setForm(prev => ({ ...prev, minimumAge: value }))}
                        placeholder="e.g., 12"
                        helperText="Provider will not see patients under this age"
                      />
                      <AgeInput
                        label="Maximum Age"
                        value={provider.form.maximumAge}
                        onChange={(value) => provider.setForm(prev => ({ ...prev, maximumAge: value }))}
                        placeholder="e.g., 80"
                        helperText="Provider will not see patients over this age"
                      />
                      <div className="space-y-3">
                        <Checkbox
                          id="hasAgeRestrictionNotes"
                          label="Add Age Restriction Disclaimer"
                          checked={showAgeDisclaimer}
                          onChange={(e) => {
                            setShowAgeDisclaimer(e.target.checked);
                            if (!e.target.checked) {
                              provider.setForm(prev => ({ ...prev, ageRestrictionNotes: '' }));
                            }
                          }}
                        />
                        {showAgeDisclaimer && (
                          <div>
                            <label 
                              htmlFor="ageRestrictionNotes" 
                              className="block text-sm font-medium mb-2"
                              style={{ color: 'var(--color-text-secondary)' }}
                            >
                              Age Restriction Disclaimer
                            </label>
                            <textarea
                              id="ageRestrictionNotes"
                              rows={3}
                              value={provider.form.ageRestrictionNotes || ''}
                              onChange={(e) => provider.setForm(prev => ({ ...prev, ageRestrictionNotes: e.target.value }))}
                              placeholder="Enter disclaimer text for age restrictions..."
                              className="w-full px-4 py-3 border rounded-lg text-sm transition-colors resize-none"
                              style={{
                                backgroundColor: 'var(--color-bg-primary)',
                                borderColor: 'var(--color-border)',
                                color: 'var(--color-text-primary)'
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-accent)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-border)';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Locations */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <MapPin className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Locations</h4>
                  </div>
                  <div className="relative location-dropdown overflow-visible">
                    <div 
                      className="min-h-[40px] border rounded-lg p-2 cursor-pointer transition-colors"
                      style={{
                        borderColor: 'var(--color-border)',
                        backgroundColor: 'var(--color-bg-primary)'
                      }}
                      onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-accent)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-border)';
                      }}
                    >
                      {provider.form.locationIds.length === 0 ? (
                        <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Select locations...</div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {provider.form.locationIds.map((locationId: string) => {
                            const location = locations.find(l => l.id === locationId);
                            return location ? (
                              <span 
                                key={locationId} 
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                style={{
                                  backgroundColor: 'var(--color-bg-tertiary)',
                                  color: 'var(--color-text-primary)'
                                }}
                              >
                                {location.name}{location.state ? `, ${location.state.code}` : ''}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleLocation(locationId);
                                  }}
                                  className="ml-1 hover:opacity-70 transition-opacity"
                                  style={{ color: 'var(--color-accent)' }}
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
                    </div>
                    {showLocationDropdown && (
                      <div 
                        className="absolute z-[75] w-full mt-1 border rounded-lg shadow-lg max-h-96 overflow-y-auto custom-scrollbar"
                        style={{
                          backgroundColor: 'var(--color-bg-primary)',
                          borderColor: 'var(--color-border)',
                          boxShadow: 'var(--shadow-lg)'
                        }}
                      >
                        <div 
                          className="sticky top-0 border-b p-2"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)'
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Search locations..."
                            value={locationSearchTerm}
                            onChange={(e) => setLocationSearchTerm(e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded focus:ring-1 transition-colors"
                            style={{
                              backgroundColor: 'var(--color-bg-primary)',
                              borderColor: 'var(--color-border)',
                              color: 'var(--color-text-primary)',
                              '--tw-ring-color': 'var(--color-accent)'
                            } as React.CSSProperties}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {filteredLocations.length === 0 ? (
                          <div className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>No locations found</div>
                        ) : (
                          <div className="p-2 grid grid-cols-5 gap-2">
                            {filteredLocations.map((location) => {
                              const isSelected = provider.form.locationIds.includes(location.id);
                              
                              return (
                                <div
                                  key={location.id}
                                  onClick={() => toggleLocation(location.id)}
                                  className="p-2 rounded border cursor-pointer transition-all relative"
                                  style={{
                                    backgroundColor: 'var(--color-bg-secondary)',
                                    borderColor: isSelected ? 'var(--color-accent)' : 'var(--color-border)',
                                    borderWidth: isSelected ? '2px' : '1px',
                                    minHeight: '100px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                                    e.currentTarget.style.borderColor = isSelected ? 'var(--color-accent)' : 'var(--color-border)';
                                  }}
                                >
                                  {isSelected && (
                                    <div className="absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center"
                                      style={{ backgroundColor: 'var(--color-accent)' }}>
                                      <span className="text-xs text-white">✓</span>
                                    </div>
                                  )}
                                  <div className="space-y-0.5">
                                    <div className="font-bold text-sm leading-tight" style={{ color: 'var(--color-text-primary)' }}>
                                      {location.name}
                                    </div>
                                    {location.physicalAddress && (
                                      <div className="text-sm leading-tight" style={{ color: 'var(--color-text-secondary)' }}>
                                        {location.physicalAddress}
                                      </div>
                                    )}
                                    {(location.city || location.state) && (
                                      <div className="text-sm leading-tight" style={{ color: 'var(--color-text-secondary)' }}>
                                        {[location.city, location.state?.code].filter(Boolean).join(', ')}
                                      </div>
                                    )}
                                    {location.zipCode && (
                                      <div className="text-sm leading-tight" style={{ color: 'var(--color-text-secondary)' }}>
                                        {location.zipCode}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Insurances */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <Shield className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Accepted Insurances</h4>
                  </div>
                  <div className="relative insurance-dropdown">
                    <div 
                      className="min-h-[40px] border rounded-lg p-2 cursor-pointer transition-colors"
                      style={{
                        borderColor: 'var(--color-border)',
                        backgroundColor: 'var(--color-bg-primary)'
                      }}
                      onClick={() => setShowInsuranceDropdown(!showInsuranceDropdown)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-accent)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-border)';
                      }}
                    >
                      {provider.form.insuranceIds.length === 0 ? (
                        <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Select insurances...</div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {provider.form.insuranceIds.map((insuranceId: string) => {
                            const insurance = insurances.find(i => i.id === insuranceId);
                            return insurance ? (
                              <span 
                                key={insuranceId} 
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                style={{
                                  backgroundColor: 'var(--color-bg-tertiary)',
                                  color: 'var(--color-text-secondary)'
                                }}
                              >
                                {insurance.name}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleInsurance(insuranceId);
                                  }}
                                  className="ml-1 transition-colors"
                                  style={{ color: 'var(--color-accent)' }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = 'var(--color-text-primary)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = 'var(--color-accent)';
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
                    </div>
                    {showInsuranceDropdown && (
                      <div 
                        className="absolute z-[60] w-full mt-1 rounded-lg shadow-lg max-h-60 overflow-y-auto custom-scrollbar"
                        style={{
                          backgroundColor: 'var(--color-bg-primary)',
                          border: '1px solid var(--color-border)',
                          boxShadow: 'var(--shadow-lg)'
                        }}
                      >
                        <div 
                          className="sticky top-0 border-b p-2"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)'
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Search insurances..."
                            value={insuranceSearchTerm}
                            onChange={(e) => setInsuranceSearchTerm(e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded focus:ring-1 transition-colors"
                            style={{
                              backgroundColor: 'var(--color-bg-primary)',
                              borderColor: 'var(--color-border)',
                              color: 'var(--color-text-primary)',
                              '--tw-ring-color': 'var(--color-accent)'
                            } as React.CSSProperties}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {availableInsurancesForDropdown.length === 0 ? (
                          <div className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                            {!provider.form.customerId
                              ? 'Select a customer to choose insurances'
                              : filteredInsurances.length === 0
                                ? 'No insurances found for this customer'
                                : 'All selected — remove one above to add it again'}
                          </div>
                        ) : (
                          availableInsurancesForDropdown.map((insurance) => (
                            <div
                              key={insurance.id}
                              onClick={() => toggleInsurance(insurance.id)}
                              className="px-3 py-2 cursor-pointer text-sm transition-colors"
                              style={{
                                backgroundColor: 'transparent',
                                color: 'var(--color-text-primary)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <span style={{ color: 'var(--color-text-primary)' }}>{insurance.name}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Languages Section */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <FileText className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Languages</h4>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      Select Languages
                    </label>
                    <div 
                      className="relative min-h-[44px] px-3 py-2 border rounded-lg cursor-pointer flex items-center flex-wrap gap-1"
                      style={{
                        backgroundColor: 'var(--color-bg-primary)',
                        borderColor: 'var(--color-border)'
                      }}
                      onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-accent)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-border)';
                      }}
                    >
                      {provider.form.languageIds.length === 0 ? (
                        <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Select languages...</div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {provider.form.languageIds.map((languageId: string) => {
                            const language = languages.find(l => l.id === languageId);
                            return language ? (
                              <span 
                                key={languageId} 
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                style={{
                                  backgroundColor: 'var(--color-bg-tertiary)',
                                  color: 'var(--color-text-secondary)'
                                }}
                              >
                                {language.name}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleLanguage(languageId);
                                  }}
                                  className="ml-1 transition-colors"
                                  style={{ color: 'var(--color-accent)' }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = 'var(--color-text-primary)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = 'var(--color-accent)';
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
                    </div>
                    {showLanguageDropdown && (
                      <div 
                        className="absolute z-[60] w-full mt-1 rounded-lg shadow-lg max-h-60 overflow-y-auto custom-scrollbar"
                        style={{
                          backgroundColor: 'var(--color-bg-primary)',
                          border: '1px solid var(--color-border)',
                          boxShadow: 'var(--shadow-lg)'
                        }}
                      >
                        <div 
                          className="sticky top-0 border-b p-2"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)'
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Search languages..."
                            value={languageSearchTerm}
                            onChange={(e) => setLanguageSearchTerm(e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded focus:ring-1 transition-colors"
                            style={{
                              backgroundColor: 'var(--color-bg-primary)',
                              borderColor: 'var(--color-border)',
                              color: 'var(--color-text-primary)',
                              '--tw-ring-color': 'var(--color-accent)'
                            } as React.CSSProperties}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {filteredLanguages.length === 0 ? (
                          <div className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>No languages found</div>
                        ) : (
                          filteredLanguages.map((language) => (
                            <div
                              key={language.id}
                              onClick={() => toggleLanguage(language.id)}
                              className="px-3 py-2 cursor-pointer text-sm transition-colors"
                              style={{
                                backgroundColor: provider.form.languageIds.includes(language.id) ? 'var(--color-accent)' : 'transparent',
                                color: 'var(--color-text-primary)'
                              }}
                              onMouseEnter={(e) => {
                                if (!provider.form.languageIds.includes(language.id)) {
                                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!provider.form.languageIds.includes(language.id)) {
                                  e.currentTarget.style.backgroundColor = provider.form.languageIds.includes(language.id) ? 'var(--color-accent)' : 'transparent';
                                }
                              }}
                            >
                              <span style={{ color: 'var(--color-text-primary)' }}>{language.name}</span>
                              {provider.form.languageIds.includes(language.id) && (
                                <span className="ml-2" style={{ color: 'var(--color-accent)' }}>✓</span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Information Section */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <FileText className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Additional Information</h4>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label 
                        htmlFor="schedulingInstructions" 
                        className="block text-sm font-medium mb-2"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        Scheduling Instructions
                      </label>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <TiptapEditor
                          key={provider.editing ? `edit-${provider.editing.id}-scheduling` : 'new-scheduling'}
                          content={provider.form.schedulingInstructions || ''}
                          onChange={(content) =>
                            provider.setForm(prev => ({
                              ...prev,
                              schedulingInstructions: normalizeRichTextHtml(content),
                            }))
                          }
                          placeholder="Enter scheduling instructions..."
                          editorMinHeight="120px"
                          className="rounded-lg border"
                        />
                      </div>
                    </div>

                    <div>
                      <label 
                        htmlFor="insuranceNotes" 
                        className="block text-sm font-medium mb-2"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        Insurance Notes
                      </label>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <TiptapEditor
                          key={provider.editing ? `edit-${provider.editing.id}-insurance` : 'new-insurance'}
                          content={provider.form.insuranceNotes || ''}
                          onChange={(content) =>
                            provider.setForm(prev => ({
                              ...prev,
                              insuranceNotes: normalizeRichTextHtml(content),
                            }))
                          }
                          placeholder="Enter insurance notes..."
                          editorMinHeight="120px"
                          className="rounded-lg border"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div 
                  className="rounded-xl p-6 mb-8 border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)'
                      }}
                    >
                      <FileText className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Provider Notes</h4>
                  </div>
                  
                  {/* Add Note */}
                  <div className="mb-6 space-y-3">
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onMouseUp={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <TiptapEditor
                        key={`provider-note-draft-${noteEditorKey}`}
                        content={newNote}
                        onChange={setNewNote}
                        placeholder="Type your note here..."
                        editorMinHeight="100px"
                        className="rounded-xl border"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={addProviderNote}
                        disabled={!noteHasContent}
                        className="px-5 py-3 rounded-xl transition-all duration-200 font-medium disabled:opacity-50 flex items-center space-x-2 shadow-sm"
                        style={{
                          backgroundColor: noteHasContent ? 'var(--color-accent)' : 'var(--color-bg-tertiary)',
                          color: noteHasContent ? 'white' : 'var(--color-text-muted)',
                          transform: noteHasContent ? 'scale(1.02)' : 'scale(1)',
                          boxShadow: noteHasContent ? '0 4px 12px rgba(59, 130, 246, 0.25)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!e.currentTarget.disabled) {
                            e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!e.currentTarget.disabled) {
                            e.currentTarget.style.backgroundColor = noteHasContent ? 'var(--color-accent)' : 'var(--color-bg-tertiary)';
                            e.currentTarget.style.transform = noteHasContent ? 'scale(1.02)' : 'scale(1)';
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Notes List */}
                  {provider.form.notes.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          Current Notes
                        </h5>
                        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          Drag to reorder • Click × to remove
                        </div>
                      </div>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={provider.form.notes.map((_, index) => `note-${index}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          {provider.form.notes.map((note, index) => (
                            <SortableNoteItem
                              key={`note-${index}`}
                              id={`note-${index}`}
                              note={note}
                              index={index}
                              onDelete={(index) => {
                                const updatedNotes = provider.form.notes.filter((_, i) => i !== index);
                                provider.setForm(prev => ({ ...prev, notes: updatedNotes }));
                              }}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </div>
                  ) : (
                    <div 
                      className="text-center py-12 rounded-xl border-2 border-dashed transition-all duration-200"
                      style={{ 
                        borderColor: 'var(--color-border)',
                        backgroundColor: 'var(--color-bg-primary)'
                      }}
                    >
                      <div 
                        className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                      >
                        <FileText className="w-8 h-8" style={{ color: 'var(--color-text-muted)' }} />
                      </div>
                      <h6 className="text-lg font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                        No notes yet
                      </h6>
                    </div>
                  )}
                </div>
                </>
                ) : (
                  <div 
                    className="text-center py-12 rounded-xl border-2 border-dashed transition-all duration-200"
                    style={{ 
                      borderColor: 'var(--color-border)',
                      backgroundColor: 'var(--color-bg-primary)'
                    }}
                  >
                    <div 
                      className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                    >
                      <Building className="w-8 h-8" style={{ color: 'var(--color-text-muted)' }} />
                    </div>
                    <h6 className="text-lg font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                      Select a customer to continue
                    </h6>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      A provider must be associated with a customer organization
                    </p>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-8 border-t transition-colors" style={{ borderColor: 'var(--color-border)' }}>
                  <button
                    type="button"
                    onClick={() => {
                      provider.handleCancel();
                      setLocationSearchTerm('');
                      setInsuranceSearchTerm('');
                      setSpecialtySearchTerm('');
                      setShowCustomerDropdown(false);
                      setShowAgeDisclaimer(false);
                    }}
                    className="flex items-center space-x-2 px-6 py-3 rounded-xl transition-colors duration-200 font-medium"
                    style={{
                      color: 'var(--color-text-primary)',
                      backgroundColor: 'var(--color-bg-secondary)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    }}
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    type="button"
                    disabled={!provider.form.customerId || provider.isCreating || provider.isUpdating}
                    onClick={(e) => provider.handleSubmit(e, conditions)}
                    className="flex items-center space-x-2 px-8 py-3 rounded-xl transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: provider.form.customerId ? 'var(--color-accent)' : 'var(--color-bg-tertiary)',
                      color: provider.form.customerId ? 'var(--color-text-on-accent)' : 'var(--color-text-muted)',
                      boxShadow: provider.form.customerId ? 'var(--shadow-lg)' : 'none',
                      cursor: provider.form.customerId ? 'pointer' : 'not-allowed'
                    }}
                    onMouseEnter={(e) => {
                      if (provider.form.customerId) {
                        e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                        e.currentTarget.style.boxShadow = 'var(--shadow-xl)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (provider.form.customerId) {
                        e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                        e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                      }
                    }}
                  >
                    <Save className="w-4 h-4" />
                    <span>{provider.editing ? 'Update Provider' : 'Create Provider'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

